import { useMemo } from 'react';

import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { useIntegrationDisplay } from '@/hooks/useIntegrationDisplay';
import { stripJobSourceTimestamp } from '@/sections/jobs/utils/jobSource.util';
import { Job } from '@/types';

export interface JobClassBreakdownRow {
  /** Bare capability id — `job.source` with its `#<timestamp>` suffix stripped. */
  id: string;
  /** Human-readable name (backend capability title first, ESLint-safe path). */
  displayName: string;
  /** Capability executor (chariot | aegis | janus | aurelian | agent | litellm), or 'unknown'. */
  executor: string;
  isIntegration: boolean;
  count: number;
}

/**
 * Groups a page of jobs by capability class for the Activity Hub.
 *
 * Pure client-side join: `stripJobSourceTimestamp(job.source)` yields the
 * capability id (job sources look like `whois#2026-07-22T10:00:00Z`), which is
 * joined against the `useGetCapabilities` record for executor + integration
 * flag, and against `useIntegrationDisplay.getDisplayName` for the display
 * name (title-first — direct `AllIntegrations[id].name` reads are
 * ESLint-banned). Unknown capabilities fall back to the stripped id with
 * executor 'unknown'. Rows are sorted by count descending.
 */
export function useJobClassBreakdown(jobs: Job[]): JobClassBreakdownRow[] {
  const { capabilities } = useGetCapabilities();
  const { getDisplayName } = useIntegrationDisplay();

  // getDisplayName is included so the React Compiler can preserve this manual
  // memo (react-hooks/preserve-manual-memoization); it is derived from the
  // same capabilities record, so the memo still keys on [jobs, capabilities].
  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const job of jobs) {
      const id = stripJobSourceTimestamp(job.source);
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }

    const capabilityByName = new Map(
      (capabilities ?? []).map(capability => [
        capability.name.toLowerCase(),
        capability,
      ])
    );

    return Array.from(counts.entries())
      .map(([id, count]): JobClassBreakdownRow => {
        const capability = capabilityByName.get(id.toLowerCase());
        return {
          id,
          displayName: getDisplayName(id),
          executor: capability?.executor ?? 'unknown',
          isIntegration: capability?.integration ?? false,
          count,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [jobs, capabilities, getDisplayName]);
}
